import { Image } from '@chakra-ui/react';
import {
  Heading,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import styled from '@emotion/styled';
import { MeetingType } from '../../../models/meeting';
import { VisitCardButton } from './VisitCardButton';
import { VisitCardButtonType } from './types';

const BORDER_TOP_RADIUS = 16;
const BORDER_BOTTOM_RADIUS = 8;

const fallbackUri = './../../../default_avatar.png';

const GradientBorder = styled('div')<{ highlighted: boolean }>`
  background: white;
  border: ${({ highlighted }) =>
    highlighted ? undefined : `1px solid #E7E7E7`};
  border-radius: ${BORDER_TOP_RADIUS}px ${BORDER_TOP_RADIUS}px
    ${BORDER_BOTTOM_RADIUS}px ${BORDER_BOTTOM_RADIUS}px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.1);
  padding: 2px;
  position: relative;
  z-index: 0;

  &::before {
    background: ${({ highlighted }) =>
      highlighted
        ? `linear-gradient(45deg, #043338 0%, #3cdbff 50%, #2d6670 100%)`
        : undefined};
    border-radius: inherit;
    content: '';
    inset: 0;
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    padding: 2px; // border thickness
    position: absolute;
    z-index: -1;
  }
`;

type Props = {
  avatarUrl: string;
  confirmed: boolean;
  highlighted?: boolean;
  hostDisplayName: string;
  loading: boolean;
  meetingId: number;
  meetingTime: string;
  meetingType: MeetingType;
  onCancel: () => void;
  onConfirm: () => void;
  onReschedule: (meetingType: MeetingType, meetingId: number) => void;
};

/**
 * Card for an upcoming visit / appointment. Shows the meeting type +
 * host, time, host avatar, and three actions (Confirm / Reschedule /
 * Cancel) rendered via `VisitCardButton`. When `confirmed` is true,
 * the Confirm button is replaced with a static "Confirmed" pill.
 *
 * The `highlighted` flag paints an animated gradient border —
 * reserved for the *next* upcoming meeting in a list.
 *
 * @example
 * <VisitCard
 *   avatarUrl={coach.avatarUrl}
 *   confirmed={false}
 *   hostDisplayName="Andrea Coach"
 *   meetingId={123}
 *   meetingTime="Tomorrow, 10:00 AM"
 *   meetingType={MeetingType.HealthCoach}
 *   loading={false}
 *   onConfirm={confirm}
 *   onReschedule={reschedule}
 *   onCancel={cancel}
 *   highlighted
 * />
 */
export const VisitCard = ({
  avatarUrl,
  confirmed,
  highlighted = false,
  hostDisplayName,
  loading,
  meetingId,
  meetingTime,
  meetingType,
  onCancel,
  onConfirm,
  onReschedule,
}: Props) => {
  return (
    <GradientBorder highlighted={highlighted}>
      <VStack
        borderTopRadius={`${BORDER_TOP_RADIUS}px`}
        borderBottomRadius={`${BORDER_BOTTOM_RADIUS}px`}
        flex={1}
        bg="white"
        justifyContent={'space-between'}
        p={4}
        maxW={'324px'}
      >
        <VStack>
          <HStack alignItems={'flex-start'} justifyContent={'space-between'}>
            <VStack>
              <Heading
                style={{ overflowWrap: 'normal' }}
                color={'secondary.500'}
                fontSize={'md'}
                fontWeight={'normal'}
                textAlign={'left'}
              >
                {meetingType}{' '}
                {hostDisplayName && (
                  <>
                    with
                    <br />
                    {hostDisplayName}
                  </>
                )}
              </Heading>
            </VStack>

            <Image
              alt="care team member profile image"
              ml={2}
              fit="cover"
              rounded={'full'}
              src={avatarUrl}
              onError={(e) => {
                e.currentTarget.src = fallbackUri;
              }}
              boxSize={'48px'}
            />
          </HStack>

          {meetingTime && (
            <Text
              color={'blueGray.600'}
              fontSize={'xs'}
              fontStyle={'normal'}
              fontWeight={'normal'}
              mt={-2}
            >
              {meetingTime}
            </Text>
          )}

          <HStack gap={1} justifyContent={'flex-start'} mt={3}>
            {confirmed ? (
              <VisitCardButton type={VisitCardButtonType.Confirmed} />
            ) : (
              <VisitCardButton
                loading={loading}
                onClick={onConfirm}
                type={VisitCardButtonType.Confirm}
              />
            )}

            <VisitCardButton
              loading={loading}
              onClick={() => onReschedule(meetingType, meetingId)}
              type={VisitCardButtonType.Reschedule}
            />

            <VisitCardButton
              loading={loading}
              onClick={onCancel}
              type={VisitCardButtonType.Cancel}
            />
          </HStack>
        </VStack>
      </VStack>
    </GradientBorder>
  );
};
